import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { CommunityServiceCategoryValidators } from 'src/app/core/validators/community-service-category.validators';
import { CommunityServiceCategoryEdit } from '../../model/community-service-category';
import { merge } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { Code } from 'src/app/core/model/dictionary';

@Component({
  standalone: false,
  selector: 'app-community-service-category-edit',
  templateUrl: './community-service-category-edit.component.html',
  styleUrls: ['./community-service-category-edit.component.scss']
})
export class CommunityServiceCategoryEditComponent implements OnInit, OnDestroy
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<CommunityServiceCategoryEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public serviceTypes: Code[] = [];

  private communityServiceCategoryEdit: CommunityServiceCategoryEdit;
  private serviceTypeUniquenessSubscription?: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService,
    private communityServiceCategoryValidators: CommunityServiceCategoryValidators)
  { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get serviceType(): UntypedFormControl
  {
    return this.editForm.get('serviceType');
  }

  public get active(): UntypedFormControl
  {
    return this.editForm.get('active');
  }

  @Input() public set model(communityServiceCategoryEdit: CommunityServiceCategoryEdit)
  {
    this.initModel(communityServiceCategoryEdit);
  }

  public ngOnInit(): void
  {
    // get list of community service types from Dictionary table
    this.dictionaryService.getCachedCommunityServiceTypes().subscribe({
      next: (data) =>
      {
        this.serviceTypes = data;
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = merge(this.communityServiceCategoryEdit, this.editForm.value);

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public ngOnDestroy(): void
  {
    this.serviceTypeUniquenessSubscription?.unsubscribe();
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(communityServiceCategory: CommunityServiceCategoryEdit)
  {
    if (communityServiceCategory === undefined)
    {
      return;
    }
    this.serviceTypeUniquenessSubscription?.unsubscribe();

    // Set the current name used for name async validators
    const previousCategoryName = this.isNew ? '' : communityServiceCategory.name;
    const previousServiceTypeId = this.isNew ? null : communityServiceCategory.serviceType.id;

    // Set form
    this.editForm = this.fb.group({
      id: [communityServiceCategory.id],
      name: [{ value: '', disabled: false }, Validators.required],
      active: { value: communityServiceCategory.active, disabled: false },
      serviceType: [{ value: communityServiceCategory.serviceType, disabled: false }, Validators.required]
    });

    const uniqueValidators = [(): Observable<ValidationErrors | null> =>
      this.communityServiceCategoryValidators
        .validateCommunityServiceCategoryNameUnique(communityServiceCategory?.id || null,
          this.editForm.get('name'), previousCategoryName,
          this.editForm.get('serviceType'), previousServiceTypeId)
    ];

    this.name.setAsyncValidators(uniqueValidators);
    this.name.updateValueAndValidity();

    this.serviceType.setAsyncValidators(uniqueValidators);
    this.serviceType.updateValueAndValidity();

    this.communityServiceCategoryEdit = communityServiceCategory;
    this.name.setValue(communityServiceCategory.name);  // This is a work around to fix asyncValidators
    this.serviceType.setValue(communityServiceCategory.serviceType);  // This is a work around to fix asyncValidators

    this.serviceTypeUniquenessSubscription = this.serviceType.valueChanges.subscribe(() =>
    {
      this.name.updateValueAndValidity({ emitEvent: false });
    });

    this.editFormActive = true;
  }

}
